/**
 * Escucha el flujo de avisos de la caseta.
 *
 * Se usa `fetch` con lectura de flujo y **no `EventSource`**, que sería lo
 * natural para SSE. `EventSource` no deja poner cabeceras, así que la receta
 * habitual es colgar el token de la URL — y nginx escribe cada URL que sirve en
 * su bitácora de acceso. Acabaríamos con sesiones de guardias en texto plano en
 * el disco del servidor, rotando durante meses. Con `fetch` el token va en
 * `Authorization`, donde va todo lo demás.
 *
 * Lo que llega es un timbre, no datos: quien lo escucha vuelve a pedir la lista
 * por el camino de siempre.
 */

import { useEffect, useRef, useState } from 'react';

import { almacenToken } from './api';
import { baseApi } from './servidor';

/**
 * Cuánto se espera un latido antes de dar la conexión por muerta.
 *
 * El servidor manda uno cada 20 s. Se deja más del doble para no reconectar por
 * una pausa del recolector de basura o un tirón de la red.
 *
 * Este vigilante es lo que hace útil al latido. Una conexión TCP puede quedarse
 * abierta y muda para siempre —un cortafuegos que la olvida, una máquina virtual
 * que se suspende— sin que `fetch` dé error nunca. Sin esto, el síntoma sería
 * una pantalla que se ve perfecta y no se entera de nada.
 */
const SIN_SENALES_MS = 45_000;

/** Espera antes de reintentar, según cuántos intentos seguidos han fallado. */
const ESPERAS_MS = [1000, 2000, 5000, 10_000];

export interface EstadoFlujo {
  /** Si hay un flujo vivo. Quien lo use puede aflojar su sondeo de respaldo. */
  conectado: boolean;
}

/**
 * Abre el flujo y llama a `alCambiar` cada vez que el servidor toca el timbre.
 *
 * Se reconecta solo, para siempre, con espera creciente. No se rinde nunca a
 * propósito: una caseta que pasó la noche sin servidor tiene que volver a estar
 * viva por la mañana sin que nadie recargue nada.
 */
export function useFlujoDeCaseta(alCambiar: () => void): EstadoFlujo {
  const [conectado, setConectado] = useState(false);

  // En una referencia y no como dependencia del efecto: si `alCambiar` se
  // recrea en cada render —lo normal—, el efecto se volvería a montar y el
  // flujo se cerraría y reabriría sin parar.
  const alCambiarRef = useRef(alCambiar);
  alCambiarRef.current = alCambiar;

  useEffect(() => {
    const cancelar = new AbortController();
    let fallos = 0;
    let vigilante: ReturnType<typeof setTimeout> | null = null;

    function detenerVigilante() {
      if (vigilante) clearTimeout(vigilante);
      vigilante = null;
    }

    async function escuchar(): Promise<void> {
      const token = almacenToken.leer();
      if (!token) return;

      const respuesta = await fetch(`${baseApi()}/api/accesos/eventos`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal: cancelar.signal,
      });

      if (!respuesta.ok || !respuesta.body) {
        throw new Error(`el flujo respondió ${respuesta.status}`);
      }

      fallos = 0;
      setConectado(true);

      const lector = respuesta.body.getReader();
      const decodificador = new TextDecoder();
      let pendiente = '';

      /** Cada señal —aviso o latido— reinicia la cuenta atrás. */
      function hayVida() {
        detenerVigilante();
        vigilante = setTimeout(() => {
          // Se cancela la lectura para que el `await lector.read()` termine y
          // el bucle de reconexión siga su curso.
          void lector.cancel().catch(() => undefined);
        }, SIN_SENALES_MS);
      }
      hayVida();

      try {
        for (;;) {
          const { done, value } = await lector.read();
          if (done) break;

          pendiente += decodificador.decode(value, { stream: true });

          // Los marcos van separados por una línea en blanco. El último trozo
          // puede venir a medias, así que se guarda para la siguiente vuelta.
          const marcos = pendiente.split('\n\n');
          pendiente = marcos.pop() ?? '';

          for (const marco of marcos) {
            hayVida();
            const evento = /^event:\s*(.+)$/m.exec(marco)?.[1]?.trim();
            // El latido solo prueba que seguimos vivos; recargar por él sería
            // volver al sondeo, y encima a un ritmo peor.
            if (evento && evento !== 'latido') alCambiarRef.current();
          }
        }
      } finally {
        detenerVigilante();
        lector.releaseLock();
      }
    }

    async function bucle(): Promise<void> {
      while (!cancelar.signal.aborted) {
        try {
          await escuchar();
        } catch (e) {
          if (cancelar.signal.aborted) return;
          // Ruido esperable: el servidor se reinició, el WiFi parpadeó. No se
          // molesta al guardia con esto en pantalla — el sondeo de respaldo
          // sigue trayendo la lista y él no tiene nada que hacer al respecto.
          console.info('[flujo] se cortó, reintentando', e);
        }
        setConectado(false);
        if (cancelar.signal.aborted) return;
        const espera = ESPERAS_MS[Math.min(fallos, ESPERAS_MS.length - 1)] ?? 10_000;
        fallos += 1;
        await new Promise((r) => setTimeout(r, espera));
      }
    }

    void bucle();

    return () => {
      cancelar.abort();
      detenerVigilante();
    };
  }, []);

  return { conectado };
}
